import {
    AccordionMultipleProps,
    Content,
    Header,
    Item,
    Root,
    Trigger,
} from '@radix-ui/react-accordion';
import cx from 'classnames';
import React, { ReactNode, useCallback, useState } from 'react';

import { IconChevronDown } from '../icons/ChevronDown';

import style from './Accordion.module.css';

type BaseAccordionItemType = {
    content: ReactNode;
    value: string;
    title: string;
};

type AccordionValue<T extends BaseAccordionItemType = BaseAccordionItemType> = Array<T['value']>;

export function useAccordion<ItemType extends BaseAccordionItemType = BaseAccordionItemType>(
    defaultValue: AccordionValue<ItemType> = [],
) {
    const [value, setValue] = useState(defaultValue);

    const onValueChange = useCallback((newValue: AccordionValue<ItemType>) => {
        setValue(newValue);
    }, []);

    return {
        value,
        setValue,
        onValueChange,
    };
}

type AccordionProps<ItemType extends BaseAccordionItemType = BaseAccordionItemType> =
    React.RefAttributes<HTMLDivElement> &
        Omit<AccordionMultipleProps, 'type'> & {
            HeaderElement?: 'div' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
            items: Array<ItemType>;
            onItemChange?: (change: { item: ItemType; index: number; isOpen: boolean }) => void;
            onValueChange: (value: AccordionValue<ItemType>) => void;
            size?: 'S' | 'M';
            value: AccordionValue<ItemType>;
        };

export function Accordion<ItemType extends BaseAccordionItemType = BaseAccordionItemType>({
    HeaderElement = 'div',
    items,
    onItemChange,
    onValueChange,
    size = 'S',
    value,
    ...props
}: AccordionProps<ItemType>) {
    const handleValueChange = (newValue: AccordionValue<ItemType>) => {
        if (onItemChange) {
            const opened = newValue.filter((val) => !value.includes(val));
            const closed = value.filter((val) => !newValue.includes(val));

            [...opened, ...closed].forEach((changedValue) => {
                const itemIndex = items.findIndex((item) => item.value === changedValue);
                if (itemIndex !== -1) {
                    onItemChange({
                        item: items[itemIndex],
                        index: itemIndex,
                        isOpen: opened.includes(changedValue),
                    });
                }
            });
        }

        onValueChange(newValue);
    };

    return (
        <Root
            className={style.list}
            data-testid="Accordion"
            {...props}
            value={value}
            onValueChange={handleValueChange}
            type="multiple"
        >
            {items.map((item) => (
                <Item key={item.value} className={style.item} value={item.value}>
                    <Header asChild>
                        <HeaderElement>
                            <Trigger
                                className={cx(style.title, {
                                    ['font-s-extrabold']: size === 'S',
                                    ['font-m-extrabold']: size === 'M',
                                })}
                            >
                                {item.title}
                                <IconChevronDown className={style.chevron} aria-hidden />
                            </Trigger>
                        </HeaderElement>
                    </Header>
                    <Content className={style.content} forceMount>
                        {item.content}
                    </Content>
                </Item>
            ))}
        </Root>
    );
}
